import React, { useContext, useMemo } from 'react';

import { JobPostingVisibleJobFragment } from '@/graphql/generated';
import { getMatchingJobAgeKey } from '@/helpers/job-age';
import { useLoginStates } from '@/hooks';

import { HeaderContextValue, HeaderProviderProps, RemoteOptionKey } from '../header.types';

export const HeaderContext = React.createContext<HeaderContextValue>({
  isApplyWithoutDocuments: false,
  isMatchingSalary: false,
  matchingJobAgeKey: undefined,
  remoteOptionKey: undefined,
  city: undefined,
  companyName: '',
  companyProfileLink: undefined,
  employmentType: undefined,
  industry: undefined,
  isSalaryEstimate: false,
  logo: undefined,
  publishedDate: undefined,
  salary: undefined,
  slug: '',
  title: undefined,
});

const hasMatchingSalary = (matchingHighlights?: JobPostingVisibleJobFragment['matchingHighlights']): boolean =>
  !!matchingHighlights?.matchingFacts.some((highlight) => highlight.type === 'job_keyfact_salary');

const getRemoteOptionKey = (
  remoteOption?: JobPostingVisibleJobFragment['remoteOptions']
): RemoteOptionKey | undefined => {
  if (!remoteOption || remoteOption.length === 0 || remoteOption.length > 1) return undefined;

  if (remoteOption[0] === 'FULL_REMOTE') return 'JOBS_REMOTE_TAG';
  if (remoteOption[0] === 'PARTLY_REMOTE') return 'JOBS_HYBRID_TAG';
  if (remoteOption[0] === 'NON_REMOTE') return 'REMOTE_OPTION_NON_REMOTE';
  return undefined;
};

export const HeaderContextProvider: React.FC<HeaderProviderProps> = ({ job, children }) => {
  const { isLoggedIn } = useLoginStates();
  const headerContextValue = useMemo(
    () => ({
      isApplyWithoutDocuments: isLoggedIn && job?.application?.__typename === 'JobXingApplication',
      matchingJobAgeKey: getMatchingJobAgeKey(job.date, job.activeUntil),
      isMatchingSalary: isLoggedIn && hasMatchingSalary(job.matchingHighlights),
      remoteOptionKey: getRemoteOptionKey(job.remoteOptions),
      city: job.location?.city,
      companyName: job.companyInfo.companyNameOverride,
      companyProfileLink: job.companyInfo.company?.links?.public,
      employmentType: job.employmentType?.type,
      industry: job?.industry?.title,
      isVerifiedByXing: job?.paid,
      isSalaryEstimate: job?.salary?.__typename === 'SalaryEstimate',
      logo: job.companyInfo.company?.logo?.url,
      publishedDate: job.date,
      salary: job?.salary,
      slug: job.slug,
      title: job.title,
    }),
    [
      isLoggedIn,
      job?.application?.__typename,
      job.date,
      job.activeUntil,
      job.matchingHighlights,
      job.remoteOptions,
      job.location?.city,
      job.companyInfo.companyNameOverride,
      job.companyInfo.company?.links?.public,
      job.companyInfo.company?.logo?.url,
      job.employmentType?.type,
      job?.industry?.title,
      job?.paid,
      job?.salary,
      job.slug,
      job.title,
    ]
  );

  return <HeaderContext.Provider value={headerContextValue}>{children}</HeaderContext.Provider>;
};

export const useHeaderContext = (): HeaderContextValue => useContext(HeaderContext);
